import { observer } from "mobx-react";
import RefundCalculatorCategoryStore, { ILinesSubtotal } from "../../../../stores/refund-calculator-category-store";
import "./refund-calculator-grid-layout.scss";
import FontBody1 from "../../../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../../components/fonts/font-props";
import FontBody2 from "../../../../../../../components/fonts/font-body2/font-body2";
import Currency from "../../../../../../../components/currency/currency";
import RefundCalculatorStore from "../../../../stores/refund-calculator-store";
import TextTruncate from "../../../../../../../components/text-truncate/text-truncate";
import { CHECKBOX_SIZES, Checkbox } from "../../../../../../../components/inputs/checkbox/checkbox";
import { TRISTATE_CHECKBOX_VALUES, TristateCheckbox } from "../../../../../../../components/inputs/tristate-checkbox/tristate-checkbox";
import { Fragment } from "react";
import useDashboardTranslation from "../../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import rmsMixpanelStore from "../../../../../../../stores/rms-mixpanel-store";
import RefundCalculatorLineStore from "../../../../stores/refund-calculator-line-store";

interface ICalculatorCategoryProps {
    calculatorStore: RefundCalculatorStore;
    showTax: boolean;
}

const RefundCalculatorGridLayout: React.FC<ICalculatorCategoryProps> = observer(({ showTax, calculatorStore }) => {
    const categoriesList = calculatorStore.refundCalculatorCategories;
    const subtotal: ILinesSubtotal = calculatorStore.getSubtotal();

    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-returns-rma-card';

    const onTristateCheckboxChange = (value: TRISTATE_CHECKBOX_VALUES, category: RefundCalculatorCategoryStore) => {
        rmsMixpanelStore.trackEvent('d&_calculator_checkbox', { RMAID: calculatorStore.rmaId });
        category.setCheckedState(value);
    }

    const onCheckboxChange = (value: boolean, line: RefundCalculatorLineStore) => {
        rmsMixpanelStore.trackEvent('d&_calculator_checkbox', { RMAID: calculatorStore.rmaId });
        line.setIsSelected(value);
    }

    return (
        <div className={`refund-calculator-layout ${showTax ? 'show-tax': ''}`}>
            <div className="price-title"
            >
                <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('PRICE', pageScope)}</FontBody1>
            </div>
            {!!showTax &&
                <div className="tax-title"
                >
                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('TAX', pageScope)}</FontBody1>
                </div>
            }
            {Object.values(categoriesList).map((category) => (
                <Fragment key={category.title}>
                    <div className={`refund-calculator-category`}
                    >
                        <TristateCheckbox
                            value={category.checkedState}
                            label=""
                            size={CHECKBOX_SIZES.SMALL}
                            onChange={(v) => onTristateCheckboxChange(v, category)}
                        />
                        <FontBody2
                            color={FONT_COLOR.PRIMARY2}
                            variant={category.getTitleVariant()}
                        >
                            {getTranslation(category.title, pageScope)}
                        </FontBody2>
                    </div>
                    {category.lines.map((line, index) => <Fragment key={index}>
                        {!category.isSingleLine && (
                            /** For single line categories (fee deductions) we don't show the line title, category title is enough */
                            <div className={`refund-calculator-grid-cell category-title`}>
                                <Checkbox
                                    label=""
                                    disabled={!line.isSelected && line.isRejected}
                                    checked={line.isSelected}
                                    onChange={(v) => onCheckboxChange(v, line)}
                                    size={CHECKBOX_SIZES.SMALL}
                                />
                                <TextTruncate maxLines={2} maxWidth={{ value: 165, type: 'px' }}>
                                    <FontBody2
                                        color={line.isRejected ? FONT_COLOR.PRIMARY3_50 : FONT_COLOR.PRIMARY2}
                                        variant={line.isSelected ? FONT_VARIANT.BOLD : FONT_VARIANT.DEFAULT}
                                    >
                                        {line.title}
                                    </FontBody2>
                                </TextTruncate>
                            </div>
                        )}
                        <div className={`refund-calculator-grid-cell amount ${category.isSingleLine ? 'single-line' : ''}`}>
                            <FontBody2
                                variant={line.isRejected ? FONT_VARIANT.LINE_TROUGH : FONT_VARIANT.DEFAULT}
                                color={line.isRejected ? FONT_COLOR.PRIMARY3_50 : FONT_COLOR.PRIMARY2}
                            >
                                <Currency
                                    amount={line.amount}
                                    currencyCode={line.amountDtoInPaymentCurrency?.currency_code}
                                />
                            </FontBody2>
                        </div>
                        {!!showTax &&
                            <div className={`refund-calculator-grid-cell tax ${category.isSingleLine ? 'single-line' : ''}`}
                            >
                                <FontBody2
                                    variant={line.isRejected ? FONT_VARIANT.LINE_TROUGH : FONT_VARIANT.DEFAULT}
                                    color={line.isRejected ? FONT_COLOR.PRIMARY3_50 : FONT_COLOR.PRIMARY2}
                                >
                                    <Currency
                                        amount={line.tax}
                                        currencyCode={line.taxDtoInPaymentCurrency?.currency_code}
                                    />
                                </FontBody2>
                            </div>
                        }
                    </Fragment>)}
                </Fragment>
            ))}
            <div className="subtotal-title">
                <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('SUBTOTAL', pageScope)}</FontBody2>
            </div>
            <div className="subtotal-amount">
                <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                    <Currency amount={subtotal.price} currencyCode={calculatorStore.paymentCurrency} />
                </FontBody2>
            </div>
            {!!showTax &&
                <div className="subtotal-tax">
                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        <Currency amount={subtotal.tax} currencyCode={calculatorStore.paymentCurrency} />
                    </FontBody2>
                </div>
            }
        </div>);
});

export default RefundCalculatorGridLayout;
